import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Plus, Pencil, Trash2, Eye, EyeOff, Star, Loader2, Building2, MapPin } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const imgUrl = (path) => `${API}/uploads/${path}`;

export default function PropertiesTab() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const token = localStorage.getItem("rk_admin_token");
    try {
      const { data } = await axios.get(`${API}/admin/properties`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data);
    } catch {
      toast.error("Could not load properties.");
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (p, changes, msg) => {
    const token = localStorage.getItem("rk_admin_token");
    setBusy(p.id);
    try {
      await axios.put(`${API}/admin/properties/${p.id}`, { ...p, ...changes }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((list) => list.map((x) => (x.id === p.id ? { ...x, ...changes } : x)));
      toast.success(msg);
    } catch {
      toast.error("Update failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}" permanently? This cannot be undone.`)) return;
    const token = localStorage.getItem("rk_admin_token");
    setBusy(p.id);
    try {
      await axios.delete(`${API}/admin/properties/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setItems((list) => list.filter((x) => x.id !== p.id));
      toast.success("Property deleted.");
    } catch {
      toast.error("Delete failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  if (items === null)
    return <div className="py-16 flex justify-center text-ink-soft"><Loader2 className="animate-spin" size={26} /></div>;

  return (
    <div data-testid="properties-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-navy">Property Listings</h2>
          <p className="mt-1 text-sm text-ink-soft">
            {items.length} {items.length === 1 ? "property" : "properties"} · Published ones appear on the website instantly.
          </p>
        </div>
        <Link
          to="/admin/properties/new"
          data-testid="add-property-button"
          className="bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-5 py-3 text-[14px] font-semibold inline-flex items-center gap-2 shadow-md shadow-gold/25"
        >
          <Plus size={16} /> Add Property
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="mt-8 bg-white rounded-xl border border-line p-14 text-center" data-testid="properties-empty-state">
          <Building2 size={30} className="mx-auto text-gold" />
          <p className="mt-4 font-display text-xl text-navy">No properties yet</p>
          <p className="mt-1.5 text-sm text-ink-soft">Add your first listing — it goes live on the website the moment you publish it.</p>
          <Link to="/admin/properties/new" className="mt-6 inline-flex items-center gap-2 bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-6 py-3 text-[14px] font-semibold">
            <Plus size={15} /> Add Property
          </Link>
        </div>
      ) : (
        <div className="mt-8 space-y-4" data-testid="admin-properties-list">
          {items.map((p) => (
            <article key={p.id} className="bg-white rounded-xl border border-line p-4 sm:p-5 flex flex-col sm:flex-row gap-4 sm:gap-5 hover:border-gold-soft transition-colors" data-testid={`admin-property-${p.id}`}>
              <div className="w-full sm:w-36 h-40 sm:h-28 rounded-lg overflow-hidden bg-warm border border-line shrink-0">
                {p.images?.length ? (
                  <img src={imgUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-ink-soft/50"><Building2 size={22} /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-display text-lg font-semibold text-navy leading-snug">{p.title}</h3>
                  {p.featured && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] bg-gold/20 text-navy border border-gold/50 rounded px-2 py-0.5">
                      <Star size={10} /> Featured
                    </span>
                  )}
                  <span className={`text-[10px] font-bold uppercase tracking-[0.1em] rounded px-2 py-0.5 border ${p.published ? "bg-navy text-white border-navy" : "bg-line/50 text-ink-soft border-line"}`} data-testid={`admin-property-status-${p.id}`}>
                    {p.published ? "Published" : "Draft"}
                  </span>
                </div>
                <p className="mt-1 text-[13px] text-ink-soft flex items-center gap-1.5">
                  <MapPin size={12} className="text-gold" /> {p.location}
                  {p.society ? ` · ${p.society}` : ""}
                </p>
                <p className="mt-1.5 text-[13px] font-medium text-ink/80">
                  {p.propertyType} · {p.listingType}
                  {p.bhk ? ` · ${p.bhk} BHK` : ""}
                  {p.areaSqft ? ` · ${p.areaSqft} sq.ft.` : ""}
                  <span className="text-gold font-semibold"> · {p.price}</span>
                </p>
                <div className="mt-3.5 flex flex-wrap gap-2">
                  <Link
                    to={`/admin/properties/${p.id}/edit`}
                    data-testid={`admin-property-edit-${p.id}`}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-navy border border-line hover:border-gold rounded-md px-3 py-2 transition-colors"
                  >
                    <Pencil size={12} /> Edit
                  </Link>
                  <button
                    onClick={() => update(p, { published: !p.published }, p.published ? "Unpublished — hidden from website." : "Published — live on website.")}
                    disabled={busy === p.id}
                    data-testid={`admin-property-publish-${p.id}`}
                    className={`inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-md px-3 py-2 border transition-colors disabled:opacity-50 ${
                      p.published ? "text-ink-soft border-line hover:border-navy" : "text-navy bg-gold/20 border-gold/50 hover:bg-gold/35"
                    }`}
                  >
                    {p.published ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}
                  </button>
                  <button
                    onClick={() => update(p, { featured: !p.featured }, p.featured ? "Removed from featured." : "Marked as featured.")}
                    disabled={busy === p.id}
                    data-testid={`admin-property-feature-${p.id}`}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-navy border border-line hover:border-gold rounded-md px-3 py-2 transition-colors disabled:opacity-50"
                  >
                    <Star size={12} /> {p.featured ? "Unfeature" : "Feature"}
                  </button>
                  <button
                    onClick={() => remove(p)}
                    disabled={busy === p.id}
                    data-testid={`admin-property-delete-${p.id}`}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-red-700 border border-red-200 hover:bg-red-50 rounded-md px-3 py-2 transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
